/**
 * Professional Logo Component
 *
 * Reusable logo with modern effects, animations, and responsive sizing.
 * Supports multiple variants, sizes, and interaction states.
 */

import { cn } from '@/lib/utils'

type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
type LogoVariant = 'default' | 'glow' | 'pulse' | 'float'

interface LogoProps {
  size?: LogoSize
  variant?: LogoVariant
  showText?: boolean
  className?: string
  onClick?: () => void
  animate?: boolean
}

const sizeConfig: Record<LogoSize, { container: string; text: string; subtext: string }> = {
  xs: { container: 'h-6 w-6', text: 'text-sm', subtext: 'text-[10px]' },
  sm: { container: 'h-8 w-8', text: 'text-base', subtext: 'text-xs' },
  md: { container: 'h-10 w-10', text: 'text-lg', subtext: 'text-xs' },
  lg: { container: 'h-14 w-14', text: 'text-xl', subtext: 'text-sm' },
  xl: { container: 'h-20 w-20', text: 'text-2xl', subtext: 'text-sm' },
  '2xl': { container: 'h-24 w-24', text: 'text-3xl', subtext: 'text-base' },
}

export function Logo({
  size = 'md',
  variant = 'default',
  showText = false,
  className,
  onClick,
  animate = true,
}: LogoProps) {
  const config = sizeConfig[size]

  const variantStyles = {
    default: '',
    glow: 'shadow-lg shadow-amber-500/30 dark:shadow-amber-500/20',
    pulse: animate ? 'animate-pulse' : '',
    float: animate ? 'animate-bounce' : '',
  }

  const containerStyles = cn(
    // Base styles
    'relative rounded-xl overflow-hidden shrink-0',
    'bg-gradient-to-br from-white via-white to-gray-50',
    'dark:from-gray-800 dark:via-gray-800 dark:to-gray-900',
    // Border and shadow
    'ring-1 ring-black/5 dark:ring-white/10',
    'shadow-md hover:shadow-lg',
    // Transitions
    'transition-all duration-300 ease-out',
    // Interactive states
    onClick && 'cursor-pointer active:scale-95 hover:scale-105',
    // Size
    config.container,
    // Variant
    variantStyles[variant],
    className
  )

  const imageStyles = cn(
    'h-full w-full object-cover',
    'transition-transform duration-500 ease-out',
    onClick && 'group-hover:scale-110'
  )

  const LogoImage = (
    <div
      className={cn('group', containerStyles)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Shimmer effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />

      <img
        src="/sloth.png"
        alt="InvoSloth"
        className={imageStyles}
        draggable={false}
      />
    </div>
  )

  if (!showText) {
    return LogoImage
  }

  return (
    <div className="flex items-center gap-3">
      {LogoImage}
      <div className="flex flex-col">
        <span className={cn('font-bold leading-tight', config.text)}>
          <span className="text-foreground/80">Invo</span>
          <span className="text-amber-500/80 dark:text-amber-300/80">Sloth</span>
        </span>
      </div>
    </div>
  )
}

/**
 * Animated Logo for splash screens and loading states
 */
export function AnimatedLogo({ size = 'xl' }: { size?: LogoSize }) {
  const config = sizeConfig[size]

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        {/* Outer ring animation */}
        <div className={cn(
          'absolute inset-0 rounded-xl',
          'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500',
          'animate-spin-slow opacity-50 blur-md',
          config.container
        )} style={{ padding: '4px' }} />

        {/* Logo */}
        <Logo size={size} variant="glow" />
      </div>

      {/* Loading dots */}
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-2 w-2 rounded-full bg-amber-500 animate-bounce"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Logo with status indicator (for showing connection status, etc.)
 */
interface LogoWithStatusProps extends LogoProps {
  status?: 'online' | 'offline' | 'busy' | 'away'
}

export function LogoWithStatus({ status, ...props }: LogoWithStatusProps) {
  const statusColors = {
    online: 'bg-green-500',
    offline: 'bg-gray-400',
    busy: 'bg-red-500',
    away: 'bg-amber-500',
  }

  return (
    <div className="relative inline-block">
      <Logo {...props} />
      {status && (
        <div className={cn(
          'absolute -bottom-0.5 -right-0.5',
          'h-3 w-3 rounded-full',
          'ring-2 ring-white dark:ring-gray-900',
          statusColors[status],
          status === 'online' && 'animate-pulse'
        )} />
      )}
    </div>
  )
}
